import { config } from "../Environment";
import type { JourneyDraft } from "./pwf";
import type { MmObjectType } from "./mentalModels";

// ── Life OS Seeder ────────────────────────────────────────────────────────────
// Types mirror the zod schema in ClaryNext-BE/services/lifeOsSeedService.js.

export interface SeedLens {
  pull: number;
  readiness: number;
  costOfWaiting: number;
}

export interface SeedSpace {
  name: string;
  identityLabel: string | null;
  vision: string | null;
  icon: string;
  color: string | null;
  traits?: string[];
}

export interface SeedProject {
  title: string;
  spaceName: string | null;
  lens: SeedLens;
  placement: "active" | "resting";
  whyNow: string | null;
}

export interface SeedActionItem {
  content: string;
  priority: "high" | "medium" | "low";
  due: "today" | "tomorrow" | "this_week" | "next_week" | null;
  spaceName: string | null;
  projectTitle?: string | null;
}

export interface SeedJourneySuggestion {
  projectTitle: string;
  reason: string;
}

export interface SeedRituals {
  morning: { enabled: boolean; time: string };
  evening: { enabled: boolean; time: string };
  weekly: { enabled: boolean; day: string; time: string };
  reason: string;
}

/** Optional smart-capture suggestions (spec 2026-11-02 §6) — at most 2 each. */
export interface SeedMentalModel {
  statement: string;
  mmType: MmObjectType;
}

export interface SeedQuality {
  name: string;
  spaceName: string | null;
}

export interface SeedPlan {
  welcomeNote: string;
  spaces: SeedSpace[];
  projects: SeedProject[];
  actionItems: SeedActionItem[];
  journeySuggestions: SeedJourneySuggestion[];
  horizon: string;
  /** 3-6 word season emphasis, e.g. "roots before wings" (v3 §3.1) */
  seasonLine?: string;
  rituals: SeedRituals;
  mentalModels?: SeedMentalModel[];
  qualities?: SeedQuality[];
}

export interface SeedFollowup {
  q: string;
  a: string;
}

export interface SeedInterview {
  areas: string[];
  alive: string;
  imported?: string;
  followups?: SeedFollowup[];
  horizonDraft?: string;
  wantsHorizonHelp?: boolean;
  existing?: { spaces?: string[]; projects?: string[]; horizon?: string };
  refine?: { previousPlan: SeedPlan; note: string };
  language?: string;
}

export interface SeedQuestion {
  question: string;
  why: string;
}

export const generateSeedQuestions = async (
  retryFetch: Function,
  interview: Pick<SeedInterview, "areas" | "alive" | "imported" | "language">
): Promise<SeedQuestion[]> => {
  const res = await retryFetch(`${config.API_BASE}/api/life-os/seed-questions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(interview),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Failed to generate questions: ${res.statusText}`);
  }
  return (await res.json()).questions as SeedQuestion[];
};

// ── Personal graduation — a life project becomes a journey ───────────────────
// The interview is synthesized server-side from the project's lens and the
// captures rooted to it; the draft is reviewed before anything materializes.

export const personalJourneyDraft = async (
  retryFetch: Function,
  projectId: string
): Promise<JourneyDraft> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/life-os/project/${projectId}/journey-draft`,
    { method: "POST", credentials: "include" }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Journey draft failed: ${res.statusText}`);
  }
  return (await res.json()).draft as JourneyDraft;
};

// ── Rituals ───────────────────────────────────────────────────────────────────

export type RitualKey = "morning" | "evening" | "weekly" | "checkin";

export interface RitualMessage {
  role: "user" | "assistant";
  content: string;
}

export type RitualSuggestionKind =
  | "create_action"
  | "complete_action"
  | "defer_action"
  | "save_reflection"
  | "place_project"
  | "create_space"
  | "update_horizon"
  | "set_active_cap";

// Typed proposal from the companion — nothing executes until the user approves.
export interface RitualSuggestion {
  kind: RitualSuggestionKind;
  label: string;
  content?: string | null;
  priority?: "high" | "medium" | "low" | null;
  dueInDays?: number | null;
  actionId?: string | null;
  days?: number | null;
  projectId?: string | null;
  placement?: "active" | "resting" | null;
  name?: string | null;
  identityLabel?: string | null;
  text?: string | null;
  capCount?: number | null;
  nowSessionId?: string | null;
  tried?: "yes" | "partly" | "no" | null;
}

export interface RitualExecuteResult {
  ok: boolean;
  label: string;
  error?: string;
}

export interface RitualTurn {
  text: string;
  suggestions: RitualSuggestion[];
  closed: boolean;
  intention: string | null;
}

// A persisted ritual conversation — transcripts are saved server-side on
// every turn, so an interrupted session can be resumed instead of lost.
export interface RitualSessionRecord {
  _id: string;
  ritualKey: RitualKey;
  status: "open" | "closed";
  messages: (RitualMessage & { suggestions?: RitualSuggestion[] })[];
  summary?: string | null;
  intention?: string | null;
  createdAt: string;
  updatedAt: string;
}

// The fresh still-open session for this ritual, or null — used to resume a
// conversation after a refresh or crashed tab.
export const getOpenRitualSession = async (
  retryFetch: Function,
  ritualKey: RitualKey
): Promise<RitualSessionRecord | null> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/life-os/ritual/session?key=${ritualKey}`,
    { credentials: "include" }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Session lookup failed: ${res.statusText}`);
  }
  return (await res.json()).session as RitualSessionRecord | null;
};

export const executeRitualSuggestions = async (
  retryFetch: Function,
  suggestions: RitualSuggestion[]
): Promise<RitualExecuteResult[]> => {
  const res = await retryFetch(`${config.API_BASE}/api/life-os/ritual/execute`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ suggestions }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Execute failed: ${res.statusText}`);
  }
  return (await res.json()).results as RitualExecuteResult[];
};

export const ritualChat = async (
  retryFetch: Function,
  ritualKey: RitualKey,
  messages: RitualMessage[],
  language: string
): Promise<RitualTurn> => {
  const res = await retryFetch(`${config.API_BASE}/api/life-os/ritual/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ ritualKey, messages, language }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Ritual turn failed: ${res.statusText}`);
  }
  return res.json();
};

export const ritualClose = async (
  retryFetch: Function,
  ritualKey: RitualKey,
  messages: RitualMessage[],
  language: string
): Promise<{ ok: boolean; summary: string }> => {
  const res = await retryFetch(`${config.API_BASE}/api/life-os/ritual/close`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ ritualKey, messages, language }),
  });
  if (!res.ok) throw new Error(`Ritual close failed: ${res.statusText}`);
  return res.json();
};

export const generateSeedPlan = async (
  retryFetch: Function,
  interview: SeedInterview
): Promise<SeedPlan> => {
  const res = await retryFetch(`${config.API_BASE}/api/life-os/seed-plan`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(interview),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Failed to generate seed plan: ${res.statusText}`);
  }
  const body = await res.json();
  return body.plan as SeedPlan;
};
